// Breadcrumb utility for dynamic navigation
import { canAccessBreadcrumb } from "./breadcrumbDestinations";
import { AI_AGENT_SUBMENU_ITEMS } from "../pages/AiAgent/aiAgentNavigation";

const buildBreadcrumbs = (pathname, schedulingLabel) => {
  if (String(pathname || "").startsWith("/app/automation/scheduled-messages")) {
    return [
      { label: "Home", path: "/app/welcomepage", isActive: false },
      { label: "CRM", path: "/app/crm", isActive: false },
      { label: "Scheduled Messages", path: pathname, isActive: true },
    ];
  }
  if (/^\/app\/scheduling\/bookings\/[^/]+\/?$/.test(String(pathname || ""))) {
    return [
      { label: "Home", path: "/app/welcomepage", isActive: false },
      { label: schedulingLabel, path: "/app/scheduling", isActive: false },
      { label: "Booking details", path: pathname, isActive: true },
    ];
  }

  if (/^\/app\/scheduling\/setup\/?$/.test(String(pathname || ""))) {
    return [
      { label: "Home", path: "/app/welcomepage", isActive: false },
      { label: "Settings", path: "/app/settings", isActive: false },
      { label: "Booking settings", path: "/app/settings/scheduling", isActive: false },
      { label: "Service setup", path: pathname, isActive: true },
    ];
  }

  if (String(pathname || "").startsWith("/app/dashboard/lead-command-centre/filter-settings")) {
    return [
      { label: "Home", path: "/app/welcomepage", isActive: false },
      { label: "Overview", path: "/app/welcomepage", isActive: false },
      {
        label: "Lead Command Centre",
        path: "/app/dashboard/lead-command-centre",
        isActive: false,
      },
      {
        label: "Lead Filter Settings",
        path: "/app/dashboard/lead-command-centre/filter-settings",
        isActive: true,
      },
    ];
  }

  if (String(pathname || "").startsWith("/app/dashboard/lead-command-centre")) {
    return [
      { label: "Home", path: "/app/welcomepage", isActive: false },
      { label: "Overview", path: "/app/welcomepage", isActive: false },
      {
        label: "Lead Command Centre",
        path: "/app/dashboard/lead-command-centre",
        isActive: true,
      },
    ];
  }

  if (String(pathname || "").startsWith("/app/crm/leads-workbench")) {
    return [
      { label: "Home", path: "/app/welcomepage", isActive: false },
      { label: "Overview", path: "/app/welcomepage", isActive: false },
      {
        label: "Lead Priorities",
        path: "/app/crm/leads-workbench",
        isActive: true,
      },
    ];
  }

  const pathSegments = pathname.split("/").filter(segment => segment !== "");

  // Remove 'app' from the beginning if it exists
  if (pathSegments[0] === "app") {
    pathSegments.shift();
  }

  const breadcrumbs = [
    { label: "Home", path: "/app/welcomepage", isActive: false },
  ];

  // If we're on the app root, return just the home breadcrumb
  if (pathSegments.length === 0) {
    breadcrumbs[0].isActive = true;
    return breadcrumbs;
  }

  const toTitleCase = value =>
    value
      .split(/[-_]+/g)
      .filter(Boolean)
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");

  // Map path segments to readable labels
  const segmentLabels = {
    dashboard: "Dashboard",
    welcomepage: "Dashboard",
    "priority-sla": "Insights",
    "lead-command-centre": "Lead Command Centre",
    scheduling: pathname.startsWith("/app/settings/") ? "Booking settings" : schedulingLabel,
    orders: "Orders",
    campaigns: "Campaigns",
    "template-campaigns-list": "Manage Campaigns",
    "template-single": "Campaign Builder",
    "template-campaign-builder": "Campaign Builder",
    "image-campaigns": "Image Campaigns",
    "assign-contacts": "Assign Contacts",
    "assigned-contacts": "Assigned Contacts",
    messaging: "Messaging",
    "send-direct-text": "Send Direct Message",
    "send-template-message": "Send Template Message",
    reporting: "Reporting",
    reports: "Campaign Analytics",
    "direct-message-history": "Message History",
    conversations: "Conversations",
    contacts: "Contacts",
    "leads-workbench": "Lead Priorities",
    settings: "Settings",
    whatsapp: "WhatsApp Api Manual Setup",
    "website-widget": "Website Widget Settings",
    integrations: "Api Key Integration",
    password: "Password",
    automation: "Automation",
    templatebuilder: "Create Template",
    "template-builder": "Create Template",
    crm: "CRM",
    catalog: "Catalog",
    inbox: "Live Chat",
    admin: "Admin",
    insights: "Insights",
    "catalog-insights": "Catalog Insights",
    "crm-insights": "CRM Insights",
    "flow-insights": "Flow Insights",
    devtools: "Developer Tools",
    "cta-tester": "CTA Tester",
    webhooks: "Webhooks",
    failed: "Failed Webhooks",
    plans: "Plans",
    "profile-update": "Profile Update",
    "cta-flow": "CTA Flow",
    "visual-builder": "Template Flow Builder",
    "flow-manager": "Flow List",
    messagelogs: "Message History",
    "cta-management": "CTA Management",
    "campaign-wizard": "Campaign Wizard",
    tracking: "Tracking",
    logs: "Logs",
    "template-campaigns": "Template Campaigns",
    "image-campaign": "Image Campaign",
    "flow-analytics": "Flow Analytics",
    "auto-reply-builder": "No-code ChaBot Builder",
    "autoreply-logs": "AutoReply Logs",
    "autoreply-test-console": "AutoReply Test Console",
    "ai-agent": "AI Agent",
    "lead-qualification": "Lead Qualification",
    debug: "Debug",
    knowledge: "AI Knowledge Base",
    "media-assets": "Media Assets",
    test: "AI Test Console",
    usage: "Usage",
    "webhook-settings": "Webhook Settings",
    "plan-manager": "Plan Manager",
    businesses: "Businesses",
    "message-history": "Message History",
    "campaign-send-logs": "Campaign Send Logs",
    list: "Campaign List",
    products: "Products",
    form: "Add Product",
    tags: "Tags",
    reminders: "Reminders",
    timeline: "Timeline",
    notes: "Notes",
    attributes: "Attributes",
    approvals: "Business Approvals",
    features: "Feature Toggles",
    "user-permissions": "User Permission Overrides",
    inboxwrapper: "Inbox",
    "send-template-simple": "Send Template",
    "whatsapp-settings": "WhatsApp Api Manual Setup",
    "profile-completion": "Profile",
    welcome: "Welcome Center",
    403: "Access Denied",
    progress: "Campaign Progress",
    "team-staff": "Team Staff",
    "manage-staff": "Manage Staff",
    "access-control": "Access Control",
    "meta-account": "WhatsApp Api Setup (Meta)",
    library: "Bot Templates",
    approved: "Templates",
    "team-management": "Team Management",
    "role-permission-mapping": "Roles & Permissions",
    billing: "Plans",
    permissions: "Permissions",
    "plan-management": "Plans",
    "account-insights": "Signup Reports",
    monitor: "Webhook Monitor",
    "signup-otp-config": "OTP Config",
    chatinbox: "Chat Inbox",
  };

  // Keep AI labels aligned with its workspace/flyout without changing other modules.
  if (pathSegments[0] === "ai-agent") {
    AI_AGENT_SUBMENU_ITEMS.forEach(item => {
      segmentLabels[item.path.split("/").pop()] = item.label;
    });
  }

  let currentPath = "/app";

  pathSegments.forEach((segment, index) => {
    currentPath += `/${segment}`;
    const isLast = index === pathSegments.length - 1;

    // Handle dynamic routes (UUIDs, IDs, etc.)
    let label = segmentLabels[segment];
    if (!label) {
      // Check if it's a UUID or ID (starts with number or contains hyphens in UUID format)
      if (
        /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
          segment
        ) ||
        /^\d+$/.test(segment)
      ) {
        label = "Details";
      } else {
        label = toTitleCase(segment);
      }
    }

    breadcrumbs.push({
      label,
      path: currentPath,
      isActive: isLast,
    });
  });

  return breadcrumbs;
};

// A null path means text, including the current page and virtual URL groups.
export const getBreadcrumbs = (pathname, auth = {}, schedulingLabel = "Bookings") =>
  buildBreadcrumbs(pathname, schedulingLabel).map(crumb => ({
    ...crumb,
    path: !crumb.isActive && crumb.label !== "Overview" && canAccessBreadcrumb(crumb.path, auth)
      ? crumb.path
      : null,
  }));
